import React from "react";
import {
  StatsigProvider,
  StatsigClient,
  StableID,
  _getInstance,
  Log,
} from "@statsig/react-bindings";
import type { StatsigUser, StatsigOptions } from "@statsig/react-bindings";
import { isCrawler } from "../isCrawler";
import { STATSIG_SDK_KEY, STATSIG_ENV_TIER } from "./statsig.constants";
import {
  UserValidationContext,
  UserValidation,
} from "../contexts/user-validation-context";

// Cached once at module load, mirroring MKTM-839 in gatsby-browser.tsx.
const isBot = isCrawler();

// Anonymous; identity (userID/email) is attached later via updateUserAsync.
const statsigUser: StatsigUser = {};

const statsigOptions: StatsigOptions = {
  environment: { tier: STATSIG_ENV_TIER || undefined },
};

// Raw `ajs_anonymous_id` cookie (a URL-safe UUID, so no decode needed) —
// buyer-experience sends the same value as its Statsig stableID, so a
// Stable-ID-randomized global_nav_v2 buckets a visitor identically in both
// apps. Undefined pre-cookie/SSR; Statsig keeps its own stableID until then.
function getAjsAnonymousId(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(/(?:^|;\s*)ajs_anonymous_id=([^;]+)/);
  return match ? match[1] : undefined;
}

// Map our auth state to a Statsig user. Anonymous stays EMPTY so Statsig keeps
// randomizing on the stableID (the ajs_anonymous_id we set via
// StableID.setOverride; see ensureStatsigClient) — marketing traffic is mostly
// anonymous, and a shared userID would collapse every anon visitor into one
// bucket. A logged-in visitor gets userID/email (+ partner) attached for
// targeting and console overrides. The SDK preserves stableID across
// updateUser, so a stableID-randomized experiment keeps the same bucket across
// the anon -> login transition on the same device.
function buildStatsigUser(user: UserValidation): StatsigUser {
  if (user.userData && user.isValid && !user.isExpired) {
    return {
      userID: user.userData.id,
      email: user.userData.emailAddress ?? undefined,
      custom: user.partner?.id ? { partnerId: user.partner.id } : undefined,
    };
  }
  return {};
}

// Anti-flicker (MOP-3826): instead of `useClientAsyncInit` (which leaves the
// client "Uninitialized" until a network round-trip resolves), initialize
// synchronously from the localStorage cache the SDK already persists
// (statsig.cached.evaluations.*). `initializeSync` reads cached evaluations
// on the spot — so returning visitors resolve their bucket on the first client
// render — then kicks off a background network refresh internally (see
// StatsigClient.updateUserSync -> _runPostUpdate). First-time visitors (no
// cache) resolve once that background refresh lands; the StatsigProvider bumps
// renderVersion on the `values_updated` event, re-running downstream hooks.
//
// This never runs during SSR: StatsigWrapper only mounts StatsigInitProvider
// for real users with a key present, and STATSIG_SDK_KEY is empty at build time
// (window.__env__ is injected at runtime), so no window/localStorage access
// happens server-side.
// Construct (or reuse) the Statsig singleton and kick a sync-from-cache init
// plus its background network refresh. Safe to call at bundle-eval time —
// gatsby-browser calls it at module scope so the config request fires as soon
// as the JS evaluates instead of waiting for <StatsigInitProvider> to mount
// (~500ms later in the load chain). Returns null for crawlers / key-less envs
// (mirrors StatsigWrapper's gating), so callers fall back to the Noop client
// (control). Idempotent: reuses the SDK's instance registry and no-ops once the
// client is past "Uninitialized".
export function ensureStatsigClient(): StatsigClient | null {
  if (typeof window === "undefined" || isBot || !STATSIG_SDK_KEY) {
    return null;
  }
  let client = _getInstance(STATSIG_SDK_KEY) as StatsigClient | undefined;
  if (!client) {
    // Align stableID with buyer-experience for cross-app bucketing, before the
    // client reads/persists its own — no-op if the cookie isn't set yet.
    const ajs = getAjsAnonymousId();
    if (ajs) StableID.setOverride(ajs, STATSIG_SDK_KEY);
    client = new StatsigClient(STATSIG_SDK_KEY, statsigUser, statsigOptions);
  }
  if (client.loadingStatus === "Uninitialized") {
    try {
      client.initializeSync();
    } catch (e) {
      Log.error(e as Error);
    }
  }
  return client;
}

// Signals that the Statsig client's identity is FINAL for this visitor — i.e.
// the experiment value won't change out from under us. useNavHold gates the nav
// reveal on this so a logging-in visitor never paints the throwaway anonymous
// bucket before the userID bucket lands (the warm + logged-in thrash). Default
// true so envs without the provider (crawlers / no-key) don't gate anything.
export const StatsigIdentityContext = React.createContext<{
  identityReady: boolean;
}>({ identityReady: true });

const StatsigInitProvider = ({ children }: { children: React.ReactNode }) => {
  // Reuses the singleton already kicked at module scope in gatsby-browser (or
  // constructs+inits it here if that path didn't run, e.g. under test).
  const client = React.useMemo(() => ensureStatsigClient(), []);
  const user = React.useContext(UserValidationContext);

  // `identityReady` gates the nav reveal. It stays false until we know the final
  // identity's bucket: anonymous visitors flip it true as soon as auth resolves
  // (nothing to attach — bucket on the existing stableID); logged-in visitors
  // flip it true only once updateUserAsync has applied the userID and Statsig
  // re-evaluated. Guarded by the last-applied userID so the anonymous majority
  // never fires a redundant network re-eval (client already inited on stableID);
  // we only call updateUserAsync on a real login (attach) or logout (reset).
  const [identityReady, setIdentityReady] = React.useState(false);
  // The identity we've kicked a live network apply for. `undefined` = none yet;
  // `null` = anonymous; a string = that userID.
  const appliedUserIdRef = React.useRef<string | null | undefined>(undefined);
  React.useEffect(() => {
    if (!client || !user.isReady) return;
    const loggedIn = !!(user.userData && user.isValid && !user.isExpired);
    const nextUserId = loggedIn ? user.userData?.id ?? null : null;
    if (appliedUserIdRef.current === nextUserId) {
      // Same identity already applied (e.g. a re-render): nothing to do.
      setIdentityReady(true);
      return;
    }
    appliedUserIdRef.current = nextUserId;

    // Apply the network result LIVE for the current identity. `initializeSync`
    // (module scope) only reads the cache, so a first-visit/cold user sits at
    // NoValues in-session and its fetch merely warms the cache for next time —
    // meaning anonymous first-timers were never assigned or exposed. updateUserAsync
    // fetches AND applies live, so cold users get their real bucket + a logged
    // exposure this session. The nav hold (useNavHold) covers the wait -> no flash.
    const apply = client
      .updateUserAsync(buildStatsigUser(user))
      .catch((e) => Log.error(e as Error));

    if (loggedIn) {
      // Logged in: keep the nav held until the userID bucket is applied, so we
      // never reveal the anonymous bucket and then flip to it (the thrash).
      setIdentityReady(false);
      apply.finally(() => setIdentityReady(true));
    } else {
      // Anonymous: identity is already final (no userID to wait for), so mark it
      // ready now. The reveal is still gated by `resolved` in useNavHold: a warm
      // anon reveals immediately from the cached bucket, while a cold anon holds
      // (NoValues) until the apply above lands, then reveals its real bucket.
      setIdentityReady(true);
    }
  }, [client, user]);

  if (!client) {
    return <>{children}</>;
  }
  return (
    <StatsigProvider client={client}>
      <StatsigIdentityContext.Provider value={{ identityReady }}>
        {children}
      </StatsigIdentityContext.Provider>
    </StatsigProvider>
  );
};

// Real users only. Crawlers and key-less envs get no provider, so the hooks
// fall back to Statsig's Noop client (control) with no init call — keeping bots
// fast and SSR/hydration anchored on control.
export const StatsigWrapper = ({ children }: { children: React.ReactNode }) => {
  if (isBot || !STATSIG_SDK_KEY) {
    return <>{children}</>;
  }
  return <StatsigInitProvider>{children}</StatsigInitProvider>;
};
